// Robust fetch wrapper that automatically retries on CSRF failures
import { getCSRFToken } from './getCSRFToken';

/**
 * Refreshes the CSRF token by fetching a new one from the server
 * @returns {Promise<string>} The new CSRF token
 */
async function refreshCSRFToken() {
  try {
    // Fetch a simple endpoint to get a fresh CSRF token
    const response = await fetch(window.location.href, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (response.ok) {
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const csrfMeta = doc.querySelector('meta[name="csrf-token"]');

      if (csrfMeta && csrfMeta.content) {
        // Update the CSRF token in the current page
        const currentCsrfMeta = document.querySelector(
          'meta[name="csrf-token"]',
        );
        if (currentCsrfMeta) {
          currentCsrfMeta.content = csrfMeta.content;
        }
        return csrfMeta.content;
      }
    }
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
  }
  return null;
}

/**
 * Fetch wrapper that automatically retries once on CSRF failure
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (must include method, headers, body, etc.)
 * @param {number} maxRetries - Maximum number of retries (default: 1)
 * @returns {Promise<Response>} The fetch response
 */
export async function fetchWithCSRFRetry(url, options = {}, maxRetries = 1) {
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Get current CSRF token
      const csrfToken = getCSRFToken();

      // Ensure headers exist and add CSRF token
      const headers = {
        ...options.headers,
        'X-CSRFToken': csrfToken,
      };

      // Make the request
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'same-origin', // Ensure cookies are sent
      });

      // If we get a 403 (CSRF error) and haven't exhausted retries, try again
      if (response.status === 403 && attempt < maxRetries) {
        console.warn(
          `CSRF token rejected (attempt ${attempt + 1}/${
            maxRetries + 1
          }). Refreshing token and retrying...`,
        );

        // Refresh the CSRF token
        const newToken = await refreshCSRFToken();
        if (!newToken) {
          console.error('Failed to refresh CSRF token. Cannot retry.');
          return response; // Return the 403 response
        }

        // Continue to next iteration to retry with new token
        continue;
      }

      // Success or non-CSRF error - return the response
      return response;
    } catch (error) {
      lastError = error;
      console.error(`Fetch attempt ${attempt + 1} failed:`, error);

      // If this was the last retry, throw the error
      if (attempt === maxRetries) {
        throw error;
      }
    }
  }

  // If we somehow get here, throw the last error
  throw lastError || new Error('Fetch failed after retries');
}

/**
 * Convenience method for POST requests with JSON body
 * @param {string} url - The URL to post to
 * @param {object} data - The data to send (will be JSON stringified)
 * @param {object} additionalOptions - Additional fetch options
 * @returns {Promise<Response>} The fetch response
 */
export async function postJSONWithCSRFRetry(url, data, additionalOptions = {}) {
  return fetchWithCSRFRetry(url, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      ...additionalOptions.headers,
    },
    ...additionalOptions,
  });
}
